import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, alpha, useTheme, Card, Stack, Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    MdAccountCircle,
    MdBusiness,
    MdSettings,
    MdNotifications,
    MdContactSupport,
    MdSecurity,
    MdPalette,
    MdAnalytics,
    MdBolt,
    MdPeople,
    MdDomain,
    MdBarChart,
    MdDescription,
    MdFactCheck,
    MdHistory
} from "react-icons/md";
import { TbActivity, TbBolt, TbRefresh } from "react-icons/tb";
import { LuCalendar } from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import { useAuth } from "../../../authentication/AuthProvider";
import { useColorMode } from "../../../theme/ThemeProviderWrapper";
import { clearUserCache } from "../../../utilities/queryClient";
import { Logout as LO } from "../../../db_access/logout";
import { User } from "../../../db_access/user";
import { Company } from "../../../db_access/company";
import { Usage } from "../../../db_access/usage";
import routing from "../../../routing";
import { getOrgTokens, ORG_PALETTE } from "../../../theme/style/organizationPalette";

// Shared components for analytics band
import DateSelector from "../../../components/input/DateSelector";

// Layout and Sub-components
import ProfileLayout from './Layout';
import GeneralTab from './GeneralTab';
import InterfaceTab from './InterfaceTab';
import SecurityTab from './SecurityTab';

// Shared Dashboards (External)
import ProfileDashboard from "../ProfileDashboard";
import UsageMine from "../../Usage/UsageMine";
import UsageCompany from "../../Usage/UsageCompany";
import UsageAllCompany from "../../Usage/UsageAllCompany";
import AdminDashboard from "../../Admin/AdminDashboard";
import NotificationDashboard from "../../Notification/NotificationDashboard";
import SupportDashboard from "../../Support/SupportDashboard";

const SummaryCard = ({ title, value, color, icon: Icon }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);

    return (
        <Box sx={{
            p: 2.5,
            borderRadius: tok.radius.card,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            flex: 1,
            minWidth: '220px',
            border: '1px solid',
            borderColor: tok.border.card,
            background: tok.bg.card,
            boxShadow: tok.shadow.card,
            transition: 'all 0.2s ease',
            '&:hover': {
                borderColor: alpha(color, 0.4),
                background: isDark ? alpha(color, 0.05) : alpha(color, 0.02),
                boxShadow: tok.shadow.cardHover,
                transform: 'translateY(-2px)'
            }
        }}>
            <Box sx={{
                p: 1.2,
                borderRadius: tok.radius.icon || 2.5,
                bgcolor: alpha(color, 0.1),
                color: color,
                display: 'flex',
                boxShadow: tok.shadow.iconBox
            }}>
                <Icon size={20} />
            </Box>
            <Box>
                <Typography variant="caption" sx={{
                    color: tok.text.muted,
                    fontWeight: tok.font.weight.bold,
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    fontSize: '11px',
                    fontFamily: tok.font.family
                }}>
                    {title}
                </Typography>
                <Typography variant="h5" sx={{
                    fontWeight: tok.font.weight.bold,
                    lineHeight: 1.1,
                    color: tok.text.primary,
                    fontFamily: tok.font.family
                }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

const UserAccountSettings = () => {
    const { t } = useTranslation();
    const { user, username, isAdmin, isCompanyAdmin, login, logout, attemptLogout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { themePreference, setThemeMode, activeMode } = useColorMode();
    const isDark = activeMode === 'dark';

    const [activeTab, setActiveTab] = useState(() => {
        // If navigation state has a specific tab index, use it
        if (location.state?.activeTab !== undefined) {
            return location.state.activeTab;
        }
        return 0;
    });
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [isVerifyingNewPassword, setIsVerifyingNewPassword] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });

    // Sync active tab if state changes after mount
    useEffect(() => {
        if (location.state?.activeTab !== undefined) {
            setActiveTab(location.state.activeTab);
        }
    }, [location.state?.activeTab]);

    // Quota State
    const [quota, setQuota] = useState({ used: 0, total: 1000 });

    // Support Access State
    const [isSupportAccessEnabled, setIsSupportAccessEnabled] = useState(false);

    // Analytics state
    const [startDate, setStartDate] = useState(dayjs().subtract(30, "days"));
    const [endDate, setEndDate] = useState(dayjs());
    const [searchText, setSearchText] = useState(""); // No longer in UI but keep in props for compat
    const [refresh, setRefresh] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [stats, setStats] = useState({ capacity: '—', modules: '—', sync: '—', totalEntries: 0 });


    const primarySolid = theme.palette.primary.main;
    const primaryAlpha = `${primarySolid}12`;

    // Fetch Quota Data and Support Access State
    useEffect(() => {
        const fetchQuota = async () => {
            try {
                const compRes = await Company.get_mine();
                const compData = await compRes.json();

                const usageRes = await Usage.getMineCompany();
                const usageData = await usageRes.json();

                const totalUsed = Array.isArray(usageData)
                    ? usageData.reduce((sum, u) => sum + (Number(u.point) || 0), 0)
                    : 0;

                if (compData?.length > 0) {
                    setQuota({
                        used: totalUsed,
                        total: compData[0].usage_limit || 1000
                    });
                }
            } catch (err) {
                console.error("Quota fetch failed", err);
            }
        };

        fetchQuota();

        // Initialize Support Access State
        if (user && !isAdmin) {
            // Since we can't call get_all, we rely on the user object from AuthProvider
            // if it was updated, or we could implement a dedicated get_my_profile.
            // For now, setting it from the current user object.
            setIsSupportAccessEnabled(!!user.is_support_access_enabled);
        }
    }, [refresh, user, isAdmin]);




    const handleToggleSupportAccess = async () => {
        try {
            const newValue = !isSupportAccessEnabled;
            // Optimistic update
            setIsSupportAccessEnabled(newValue);

            const res = await User.update({
                id: user.id,
                is_support_access_enabled: newValue ? 1 : 0
            });

            const data = await res.json();

            if (res.status === 200) {
                setMessage({ text: `Support access ${newValue ? 'enabled' : 'disabled'}`, type: "success" });

                // Update global user context by updating localStorage and calling login()
                const updatedUser = { ...user, is_support_access_enabled: newValue ? 1 : 0 };
                login(updatedUser);

                // Trigger a refresh of any components listening for notification-like state changes
                window.dispatchEvent(new Event('notificationUpdate'));
            } else {
                setIsSupportAccessEnabled(!newValue); // Revert
                setMessage({ text: data.message || "Failed to update setting", type: "error" });
            }
        } catch (error) {
            setIsSupportAccessEnabled(!isSupportAccessEnabled); // Revert
            setMessage({ text: "An error occurred", type: "error" });
        }
    };

    const handleRefresh = () => {
        setIsSyncing(true);
        setRefresh(prev => !prev);
        setTimeout(() => setIsSyncing(false), 800);
    };

    const handleVerifyCurrentPassword = () => {
        if (!currentPassword) {
            setMessage({ text: "Please enter your current password", type: "error" });
            return;
        }
        setIsVerifyingNewPassword(true);
        setMessage({ text: "", type: "" });
    };

    const handleChangePassword = async () => {
        // ... same logic ...
        if (!newPassword || !confirmPassword) {
            setMessage({ text: "Please fill all fields", type: "error" });
            return;
        }
        if (newPassword !== confirmPassword) {
            setMessage({ text: "Passwords do not match", type: "error" });
            return;
        }
        if (newPassword.length < 6) {
            setMessage({ text: "Password must be at least 6 characters", type: "error" });
            return;
        }

        try {
            const rawRes = await User.change_password({
                id: user.id,
                current_password: currentPassword,
                new_password: newPassword
            });
            const res = await rawRes.json();
            if (res.status === "success" || res.message === "Password updated successfully" || !res.message) {
                setMessage({ text: "Password updated successfully", type: "success" });
                setNewPassword("");
                setConfirmPassword("");
                setCurrentPassword("");
                setIsVerifyingNewPassword(false);
            } else {
                setMessage({ text: res.message || "Failed to update password", type: "error" });
            }
        } catch (error) {
            setMessage({ text: "An error occurred", type: "error" });
        }
    };

    const handleLogout = async () => {
        try {
            await clearUserCache();
            LO.clear_token().finally(() => {
                logout();
                navigate(routing.login);
            });
        } catch (error) {
            console.error("Logout failed", error);
            logout();
            navigate(routing.login);
        }
    };

    const menuGroups = [
        {
            title: "Account",
            items: [
                { label: "General", icon: <MdAccountCircle />, index: 0 },
                { label: "Security", icon: <MdSecurity />, index: 6 },
            ]
        },
        {
            title: "Analytics",
            items: [
                { label: "My Usage", icon: <MdBolt />, index: 1 },
                ...(isCompanyAdmin ? [{ label: "Company Usage", icon: <MdBarChart />, index: 10 }] : []),
                ...(isAdmin ? [{ label: "Platform Usage", icon: <MdAnalytics />, index: 11 }] : []),
            ]
        },
        {
            title: isAdmin ? "Master Admin" : "Management",
            items: [
                ...(isCompanyAdmin && !isAdmin ? [
                    { label: "Organization Info", icon: <MdBusiness />, index: 2 },
                ] : []),
                ...(isAdmin ? [
                    { label: "Organizations", icon: <MdDomain />, index: 9 },
                    { label: "User Management", icon: <MdPeople />, index: 5 },
                    { label: "Login History", icon: <MdHistory />, index: 14 },
                ] : []),
            ]
        },
        ...(isAdmin ? [{
            title: "Platform Control",
            items: [
                { label: "Template Management", icon: <MdDescription />, index: 12 },
                { label: "Validation Management", icon: <MdFactCheck />, index: 13 },
            ]
        }] : []),
        {
            title: "Preferences",
            items: [
                { label: "Notifications", icon: <MdNotifications />, index: 3 },
            ]
        },
        {
            title: null,
            items: [
                { label: "Support", icon: <MdContactSupport />, index: 4 },
            ]
        }
    ];

    const renderAnalyticsBand = () => {
        return null; // Removed as requested to prioritize Core Drivers at the top
    };

    const renderContent = () => {
        const usageProps = {
            startDate,
            endDate,
            setStartDate,
            setEndDate,
            searchText,
            refresh,
            setGlobalStats: setStats
        };

        return (
            <Box>
                {(() => {
                    switch (activeTab) {
                        case 0: // General
                            return (
                                <GeneralTab
                                    t={t}
                                    mode={themePreference}
                                    user={user}
                                    username={username}
                                    primarySolid={primarySolid}
                                    setThemeMode={setThemeMode}
                                    activeMode={activeMode}
                                />
                            );
                        case 1: // My Usage
                            return <UsageMine {...usageProps} />;
                        case 10: // Company Usage
                            return <UsageCompany {...usageProps} />;
                        case 11: // Platform Usage
                            return <UsageAllCompany {...usageProps} />;
                        case 2: // Organization Info
                            if (isAdmin) return <AdminDashboard initialTab="Organizations" noPadding={true} hideTabs={true} />;
                            return isCompanyAdmin ? <ProfileDashboard /> : null;
                        case 3: // Notifications
                            return <NotificationDashboard />;
                        case 4: // Support
                            return <SupportDashboard />;
                        case 5: // User Management
                            return isAdmin ? <AdminDashboard initialTab="User Management" noPadding={true} hideTabs={true} /> : null;
                        case 9: // Organizations
                            return isAdmin ? <AdminDashboard initialTab="Organizations" noPadding={true} hideTabs={true} /> : null;
                        case 12: // Template Management
                            return isAdmin ? <AdminDashboard initialTab="Template Management" noPadding={true} hideTabs={true} /> : null;
                        case 13: // Validation Management
                            return isAdmin ? <AdminDashboard initialTab="Validation Management" noPadding={true} hideTabs={true} /> : null;
                        case 14: // Login History
                            return isAdmin ? <AdminDashboard initialTab="Login History" noPadding={true} hideTabs={true} /> : null;
                        case 6: // Security
                            return (
                                <SecurityTab
                                    t={t}
                                    mode={activeMode}
                                    currentPassword={currentPassword}
                                    setCurrentPassword={setCurrentPassword}
                                    newPassword={newPassword}
                                    setNewPassword={setNewPassword}
                                    confirmPassword={confirmPassword}
                                    setConfirmPassword={setConfirmPassword}
                                    isVerifyingNewPassword={isVerifyingNewPassword}
                                    setIsVerifyingNewPassword={setIsVerifyingNewPassword}
                                    handleVerifyCurrentPassword={handleVerifyCurrentPassword}
                                    handleChangePassword={handleChangePassword}
                                    message={message}
                                    isSupportAccessEnabled={isSupportAccessEnabled}
                                    handleToggleSupportAccess={handleToggleSupportAccess}
                                />
                            );

                        default:
                            return null;
                    }
                })()}
            </Box>
        );
    };

    return (
        <ProfileLayout
            user={user}
            username={username}
            isAdmin={isAdmin}
            isCompanyAdmin={isCompanyAdmin}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            menuGroups={menuGroups}
            attemptLogout={attemptLogout}
            handleLogout={handleLogout}
            mode={activeMode}
            primarySolid={primarySolid}
            primaryAlpha={primaryAlpha}
            quotaData={quota}
        >
            {renderContent()}
        </ProfileLayout>
    );
};

export default UserAccountSettings;
